import { CarouselSlides, ProgressiveScrollSnapBar, useAutoCarouselController } from '@components/display/emblaCarousel';
import { VictronImage } from '@components/display/images';
import { YouTubeVideoDialog } from '@components/display/videos';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import {
    Box,
    Button,
    ButtonBase,
    Fade,
    IconButton,
    ButtonProps as MUIButtonProps,
    Skeleton,
    Typography,
} from '@mui/material';
import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import { ReactNode, useEffect, useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import type { LinkProps as RouterLinkProps } from 'react-router-dom';

export type BannerButtonProps<C extends React.ElementType = 'button'> = Omit<
    MUIButtonProps,
    'component' | 'LinkComponent'
> & {
    component?: C;
    LinkComponent?: C;
} & Partial<Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof MUIButtonProps>> &
    Partial<
        Omit<RouterLinkProps, keyof MUIButtonProps | keyof React.AnchorHTMLAttributes<HTMLAnchorElement> | 'children'>
    >;

export type BannerData = {
    background?: 'blue' | 'dark' | 'light-blue';
    header_superscript?: ReactNode;
    header_title?: React.ReactNode;
    header_subtitle?: React.ReactNode;
    header_text?: React.ReactNode;
    button_text?: React.ReactNode;
    button_link?: string;
    video_id?: string;
    image?: string;
    image_alt?: string | MessageDescriptor;
    loading?: boolean;
    draft?: boolean;
};

type BannerCollection = {
    items: BannerData[];
    count?: number;
};

type BannerProps<C extends React.ElementType = 'button'> = {
    className?: string;
    loading?: boolean;
    data: BannerData | BannerCollection;
    onSlideIndexChange?: (index: number) => void;
    buttonProps?: BannerButtonProps<C>;
    imgProps?: React.ImgHTMLAttributes<HTMLImageElement>;
};

const isBannerDataCollection = (data: BannerData | BannerCollection): data is BannerCollection => {
    return typeof data === 'object' && data !== null && 'items' in data && Array.isArray(data.items);
};

const getBannerLinkProps = <C extends React.ElementType>(
    buttonProps: BannerButtonProps<C> | undefined,
    bannerLink: RouterLinkProps['to'] | undefined
): Record<string, unknown> =>
    buttonProps?.LinkComponent
        ? {
              ...buttonProps,
              LinkComponent: buttonProps.LinkComponent,
              to: buttonProps.to ?? bannerLink,
          }
        : {
              ...buttonProps,
              LinkComponent: 'a',
              href: buttonProps?.href ?? (typeof bannerLink === 'string' ? bannerLink : undefined),
          };

export const Banner = <C extends React.ElementType = 'button'>({
    className,
    loading,
    data,
    onSlideIndexChange,
    buttonProps,
    imgProps,
}: BannerProps<C>) => {
    const [openVideoId, setOpenVideoId] = useState<string | null>(null);
    const { emblaRef, autoplay, selectedIndex, scrollSnaps, onBarButtonClick } = useAutoCarouselController({
        delay: 5000,
        autoplayProps: {
            active: !openVideoId,
        },
    });
    const [emblaDotsRef, emblaDotsApi] = useEmblaCarousel({
        containScroll: 'keepSnaps',
        dragFree: true,
        watchDrag: (api) => api.canScrollNext() || api.canScrollPrev(),
    });

    const [displayedTextIndex, setDisplayedTextIndex] = useState(0);
    const [showText, setShowText] = useState(true);

    const intl = useIntl();

    const isBannerDataCollectionResult = isBannerDataCollection(data);
    const bannerItems = isBannerDataCollectionResult ? data.items : [data];
    const isMultiBanner = isBannerDataCollectionResult && bannerItems.length > 1;
    const bannerScrollSnaps = bannerItems.map((_, index) => scrollSnaps[index] ?? index);
    const activeDotIndex = Math.min(selectedIndex, bannerScrollSnaps.length - 1);
    const activeBanner = bannerItems[selectedIndex] ?? bannerItems[0];
    const isLoading = Boolean(loading || activeBanner?.loading);
    const bannerLink = buttonProps?.to ?? buttonProps?.href ?? activeBanner?.button_link;
    const isBannerClickable = Boolean(bannerLink && !isLoading);

    useEffect(() => {
        if (!isMultiBanner || bannerItems.length === 0) {
            return;
        }

        if (selectedIndex === displayedTextIndex) {
            return;
        }

        setShowText(false);
        const timer = window.setTimeout(() => {
            setDisplayedTextIndex(selectedIndex);
            setShowText(true);
        }, 160);

        return () => {
            window.clearTimeout(timer);
        };
    }, [bannerItems, displayedTextIndex, isMultiBanner, selectedIndex]);

    useEffect(() => {
        onSlideIndexChange?.(selectedIndex);
        if (isMultiBanner) {
            emblaDotsApi?.scrollTo(selectedIndex, true);
        }
    }, [isMultiBanner, onSlideIndexChange, selectedIndex]);

    return (
        <div className={`w-full overflow-hidden ${className ?? ''}`}>
            <div
                className={clsx(
                    'relative flex flex-col rounded-lg lg:flex-row lg:justify-between lg:gap-4 w-full h-full',
                    {
                        'bg-[#004899]': isLoading || activeBanner?.background === 'blue',
                        'bg-gradient-to-b from-[#151821] to-black': activeBanner?.background === 'dark',
                        'bg-light-blue-gradient': activeBanner?.background === 'light-blue',
                    }
                )}
            >
                {isBannerClickable && (
                    <ButtonBase
                        {...getBannerLinkProps(buttonProps, bannerLink)}
                        className="absolute inset-0 z-0 rounded-lg"
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 0,
                            display: 'block',
                            width: '100%',
                            height: '100%',
                            borderRadius: 'inherit',
                            '&.Mui-focusVisible': {
                                outline: '2px solid currentColor',
                                outlineOffset: '-2px',
                            },
                        }}
                    />
                )}
                <div
                    className={clsx(
                        'relative z-[1] flex flex-col gap-4 px-6 py-6 justify-center',
                        isBannerClickable && 'pointer-events-none',
                        {
                            'lg:w-1/2': activeBanner?.image || activeBanner?.video_id || isLoading,
                        }
                    )}
                >
                    <div className="flex flex-col gap-4">
                        {isLoading ? (
                            <Skeleton variant="rounded" sx={{ bgcolor: '#fff' }} height={40} />
                        ) : (
                            activeBanner?.header_superscript && (
                                <Fade in={showText} timeout={{ enter: 250, exit: 160 }}>
                                    <Typography fontWeight={400} fontSize={16} color="#999A9E">
                                        {activeBanner?.header_superscript}
                                    </Typography>
                                </Fade>
                            )
                        )}
                        {isLoading ? (
                            <Skeleton variant="rounded" sx={{ bgcolor: '#fff' }} height={40} />
                        ) : (
                            <Fade in={showText} timeout={{ enter: 250, exit: 160 }}>
                                <Box>
                                    <p className="font-semibold text-xl sm:text-[1.5rem] lg:text-[2rem] leading-[1.1] tracking-[-0.05em] font-museo text-white line-clamp-2">
                                        {activeBanner?.header_title}
                                    </p>
                                    {activeBanner?.header_subtitle && (
                                        <Typography
                                            fontFamily="museo-sans"
                                            fontSize={32}
                                            letterSpacing={-0.8}
                                            color="primary-200"
                                        >
                                            {activeBanner.header_subtitle}
                                        </Typography>
                                    )}
                                </Box>
                            </Fade>
                        )}
                        {isLoading ? (
                            <Skeleton variant="rounded" sx={{ bgcolor: '#fff' }} height={120} />
                        ) : (
                            activeBanner?.header_text && (
                                <Fade in={showText} timeout={{ enter: 250, exit: 160 }}>
                                    <Typography fontSize={16} color="white">
                                        {activeBanner?.header_text}
                                    </Typography>
                                </Fade>
                            )
                        )}
                    </div>
                    {buttonProps &&
                        (isLoading ? (
                            <Skeleton variant="rounded" height={36} width={120} sx={{ bgcolor: '#fff' }} />
                        ) : (
                            <Button
                                variant="contained"
                                color={
                                    activeBanner?.background &&
                                    ['blue', 'light-blue'].includes(activeBanner?.background)
                                        ? 'black'
                                        : 'primary'
                                }
                                {...buttonProps}
                                href={
                                    buttonProps?.LinkComponent
                                        ? buttonProps.href
                                        : buttonProps?.href ?? activeBanner?.button_link
                                }
                                className={clsx(
                                    buttonProps?.className,
                                    'self-start rounded-full normal-case text-[0.82rem] font-medium px-4 py-1.5',
                                    isBannerClickable && 'pointer-events-auto'
                                )}
                            >
                                {activeBanner?.button_text}
                            </Button>
                        ))}
                    {isBannerDataCollectionResult && data.items.length > 0 && (
                        <ProgressiveScrollSnapBar
                            selectedIndex={activeDotIndex}
                            scrollSnaps={bannerScrollSnaps}
                            onBarButtonClick={onBarButtonClick}
                            buttonProps={{
                                delay: 5000,
                                autoplay,
                                variant: 'dot',
                            }}
                            className={clsx('mt-auto', isBannerClickable && 'pointer-events-auto')}
                            carousel={{
                                ref: emblaDotsRef,
                            }}
                        />
                    )}
                </div>
                {activeBanner?.image || activeBanner?.video_id || isLoading ? (
                    <div
                        className={clsx(
                            'relative flex items-center max-w-[549px] mx-auto my-auto px-4 pb-4 lg:py-4 lg:mx-0 w-full',
                            {
                                'lg:w-1/2': activeBanner?.image || activeBanner?.video_id || isLoading,
                            }
                        )}
                    >
                        {isLoading && !isMultiBanner ? (
                            <Skeleton
                                variant="rounded"
                                width="100%"
                                height={260}
                                sx={{
                                    bgcolor: '#fff',
                                }}
                            />
                        ) : isMultiBanner ? (
                            <div className="w-full lg:pr-4 relative">
                                <Box
                                    overflow="hidden"
                                    ref={emblaRef}
                                    width="100%"
                                    className={isBannerClickable ? 'pointer-events-auto' : undefined}
                                >
                                    <CarouselSlides
                                        slides={
                                            data?.items.map((banner) => ({
                                                ...banner,
                                                src:
                                                    banner.image ??
                                                    (banner.video_id
                                                        ? `https://img.youtube.com/vi/${banner.video_id}/hqdefault.jpg`
                                                        : ''),
                                            })) || []
                                        }
                                        renderSlideOverlay={(index) => {
                                            const videoId = data.items[index]?.video_id;
                                            if (!videoId || loading || data.items[index]?.loading) {
                                                return null;
                                            }

                                            return (
                                                <IconButton
                                                    aria-label="Play video"
                                                    onClick={() => setOpenVideoId(videoId)}
                                                    className={clsx(
                                                        'absolute-center',
                                                        isBannerClickable && 'pointer-events-auto'
                                                    )}
                                                    size="large"
                                                    sx={{
                                                        bgcolor: 'primary-500.main',
                                                        '&:hover': {
                                                            bgcolor: 'primary-700.main',
                                                        },
                                                        color: 'white.main',
                                                    }}
                                                >
                                                    <PlayArrowRounded fontSize="large" />
                                                </IconButton>
                                            );
                                        }}
                                        gradient={false}
                                        className="aspect-[2/1] bg-white h-[260px]"
                                    />
                                </Box>
                            </div>
                        ) : activeBanner?.image || activeBanner?.video_id ? (
                            <VictronImage
                                src={
                                    activeBanner.image ??
                                    `https://img.youtube.com/vi/${activeBanner.video_id}/hqdefault.jpg`
                                }
                                alt={
                                    typeof activeBanner?.image_alt === 'string'
                                        ? activeBanner?.image_alt || 'Overview Banner Preview'
                                        : activeBanner?.image_alt
                                        ? intl.formatMessage(activeBanner?.image_alt)
                                        : 'Overview Banner Preview'
                                }
                                {...imgProps}
                                className={clsx('rounded-lg object-cover w-full aspect-[2/1]', imgProps?.className, {
                                    'bg-white h-[260px]': !isLoading,
                                })}
                            />
                        ) : null}
                        {!isLoading &&
                            (!isBannerDataCollectionResult || bannerItems.length === 1) &&
                            activeBanner?.video_id && (
                                <IconButton
                                    aria-label="Play video"
                                    onClick={() => setOpenVideoId(activeBanner.video_id ?? null)}
                                    className={clsx('absolute-center', isBannerClickable && 'pointer-events-auto')}
                                    size="large"
                                    sx={{
                                        bgcolor: 'primary-500.main',
                                        '&:hover': {
                                            bgcolor: 'primary-700.main',
                                        },
                                        color: 'white.main',
                                    }}
                                >
                                    <PlayArrowRounded fontSize="large" />
                                </IconButton>
                            )}
                    </div>
                ) : null}
            </div>
            <YouTubeVideoDialog videoId={openVideoId} onClose={() => setOpenVideoId(null)} />
        </div>
    );
};
